import { Col, Spinner2 } from 'components';
import { Api, theme, CTA } from 'lib';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CallToActionModal from 'screens/Profile/components/CallToActionModal';
import ReferralFlow from './components/ReferralFlow';

const REFERRAL_CTA_ACTION = 'fancam-challenge';

function buildCTARoute(screen, params, shortCode) {
  if (!screen) return '/';

  const { username, ctaAction, uuid, userId, tiri, team } = params ?? {};

  switch (screen) {
    case 'StackProfile':
      if (username && ctaAction && userId) return `/subscription-plans?teamId=${userId}`;
      // if (username && ctaAction && uuid) return `/profile/${username}/${ctaAction}/${uuid}`;
      if (username && ctaAction) return `/profile/${username}/${ctaAction}`;
      if (username) return `/profile/${username}`;
      return '/';

    case 'StackPost':
      return uuid ? `/post/${uuid}` : '/';

    // Rate It referral links, minted by the mobile share screen and by this app's own share
    // step with { team, tiri, senderId, referralType: 'rateit' }. Without this case they fall
    // through to '/' — the link opens the web app and does nothing.
    //
    // `tiri` is the SHARED moment and is authoritative: the feed is a random draw that will
    // usually not contain it, so it rides in the path rather than being looked up later.
    //
    // `ref` carries the SHORTCODE, not `senderId`. The landing needs the shortCode to read the
    // referrer's name and the reward figure from the public deeplink endpoint, and the activate
    // endpoint takes only a shortCode by design — it reads the referrer server-side, so there is
    // deliberately nothing here for a client to forge. The link's own `senderId` is therefore
    // never read on this side at all.
    case 'RateIt': {
      if (!tiri) return '/';

      const query = new URLSearchParams();
      if (shortCode) query.set('ref', shortCode);
      if (team) query.set('team', team);
      const qs = query.toString();

      return `/rate-it/${tiri}${qs ? `?${qs}` : ''}`;
    }

    default:
      return '/';
  }
}

const DeepLink = () => {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loggedInOnce = useSelector((state) => state.appState.loggedInOnce);
  const currentUser = useSelector((state) => state.user);
  const isLoggedIn = currentUser !== null;

  const [ctaModalData, setCtaModalData] = useState(null); // { cta, otherUser, ctaRoute }
  const [referralData, setReferralData] = useState(null); // { senderId, username, shareToken, fancamId, origin }

  useEffect(() => {
    resolveAndRedirect();
  }, [shortCode]);

  async function resolveAndRedirect() {
    try {
      const data = await Api.get(`/deeplink/${shortCode}`);
      const ctaRoute = buildCTARoute(data.screen, data.params, shortCode);
      const { username, ctaAction } = data.params ?? {};

      // sendShoutout has a dedicated screen that handles both auth states — skip the CTAModal.
      // Guests can't use the navigateTo mechanism below (it's only consumed by the User
      // container), so navigate directly for them.
      if (
        data.screen === 'StackProfile' &&
        username &&
        ctaAction &&
        ctaAction === CTA.SEND_SHOUTOUT
      ) {
        const sendShoutoutRoute = `/shoutout/${username}`;

        if (isLoggedIn && !loggedInOnce) {
          dispatch({
            type: 'UPDATE_USER',
            payload: { navigateTo: sendShoutoutRoute.replace(/^\//, '') }
          });
        } else {
          navigate(sendShoutoutRoute, { replace: true });
        }
        return;
      }

      // Receiver-side referral flow (Figma Screens 4-7) — fully separate from
      // CallToActionModal (and its normalizeActionType collision with the
      // unrelated 'fancamChallenge' CTA). Matches the exact raw string only.
      //
      // A referral link always carries `senderId`. The profile's "Activate Fancam Challenge" CTA
      // share uses the same `fancam-challenge` action with only `{ username, ctaAction }`; without
      // a sender there is no one to refer from, so it falls through to the ordinary CTA handling.
      const { senderId, shareToken, fancamId, origin } = data.params ?? {};
      if (
        data.screen === 'StackProfile' &&
        username &&
        ctaAction === REFERRAL_CTA_ACTION &&
        senderId
      ) {
        // `fancamId` marks a "View My FanCam Post" link: the referral is for that one post.
        setReferralData({ senderId, username, shareToken, fancamId, origin });
        return;
      }

      // Rate It links resolve for signed-out visitors too, and must navigate directly for them.
      // The generic tail below dispatches `navigateTo`, which only `User.js` consumes — a guest
      // never sees it and would sit on this spinner forever. Same shape as the sendShoutout
      // branch above, and for the same reason.
      if (data.screen === 'RateIt') {
        if (isLoggedIn && !loggedInOnce) {
          dispatch({
            type: 'UPDATE_USER',
            payload: { navigateTo: ctaRoute.replace(/^\//, '') }
          });
        } else {
          navigate(ctaRoute, { replace: true });
        }
        return;
      }

      // For unauthenticated users on a profile CTA deeplink, show the CTAModal first
      if (!isLoggedIn && data.screen === 'StackProfile' && username && ctaAction) {
        const otherUser = await Api.get(`/users/getByUsername/${username}`);
        if (otherUser) {
          setCtaModalData({ cta: ctaAction, otherUser, ctaRoute });
          return;
        }
      }

      if (!loggedInOnce) {
        // User.js hasn't done its initial navigation yet — store the target
        // so User.js picks it up via the navigateTo mechanism
        dispatch({
          type: 'UPDATE_USER',
          payload: { navigateTo: ctaRoute.replace(/^\//, '') }
        });
      } else {
        navigate(ctaRoute, { replace: true });
      }
    } catch {
      navigate('/');
    }
  }

  function handleCloseCTAModal() {
    setCtaModalData(null);
    navigate('/');
  }

  if (referralData) {
    return (
      <ReferralFlow
        senderId={referralData.senderId}
        username={referralData.username}
        shareToken={referralData.shareToken}
        fancamId={referralData.fancamId}
        origin={referralData.origin}
      />
    );
  }

  if (ctaModalData) {
    return (
      <>
        <Col wid="100%" ht="calc(100vh - 74px)" noFlex centerAll>
          <Spinner2 col={theme.SECONDARY} />
        </Col>
        <CallToActionModal
          isVisible
          onCloseModal={handleCloseCTAModal}
          cta={ctaModalData.cta}
          otherUser={ctaModalData.otherUser}
          setOtherUser={(updater) =>
            setCtaModalData((prev) => ({
              ...prev,
              otherUser: typeof updater === 'function' ? updater(prev.otherUser) : updater
            }))
          }
          isFollowing={false}
          isSkipToSignUp={false}
          setShoutoutSelection={() => {}}
          isPaymentRequired={false}
          setPaymentModalVisible={() => {}}
          deeplinkPath={ctaModalData.ctaRoute}
        />
      </>
    );
  }

  return (
    <Col wid="100%" ht="calc(100vh - 74px)" noFlex centerAll>
      <Spinner2 col={theme.SECONDARY} />
    </Col>
  );
};

export default DeepLink;
